(() => {
  const hydratedMediaRoots = new WeakSet();
  const observedMediaRoots = new WeakSet();
  let mediaObserver = null;

  const tryAutoplayHeroVideo = (video) => {
    if (!(video instanceof HTMLVideoElement)) return;
    if (!video.hasAttribute('autoplay')) return;

    video.muted = true;

    const attemptPlay = () => {
      video.play?.().catch(() => {});
    };

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      attemptPlay();
      return;
    }

    video.addEventListener('canplay', attemptPlay, { once: true });
    attemptPlay();
  };

  const hydrateMediaRoot = (mediaRoot) => {
    if (hydratedMediaRoots.has(mediaRoot)) return;

    hydratedMediaRoots.add(mediaRoot);

    const sourceNodes = mediaRoot.querySelectorAll('source[data-srcset], source[data-src]');
    for (const sourceNode of sourceNodes) {
      const sourceSet = sourceNode.getAttribute('data-srcset');
      if (sourceSet) {
        sourceNode.setAttribute('srcset', sourceSet);
        sourceNode.removeAttribute('data-srcset');
      }

      const sourceSrc = sourceNode.getAttribute('data-src');
      if (sourceSrc) {
        sourceNode.setAttribute('src', sourceSrc);
        sourceNode.removeAttribute('data-src');
      }
    }

    const deferredImage = mediaRoot.querySelector('img[data-deferred-image]');
    if (deferredImage) {
      const deferredSrcSet = deferredImage.getAttribute('data-srcset');
      if (deferredSrcSet) {
        deferredImage.setAttribute('srcset', deferredSrcSet);
        deferredImage.removeAttribute('data-srcset');
      }

      const deferredSrc = deferredImage.getAttribute('data-src');
      if (deferredSrc) {
        deferredImage.setAttribute('src', deferredSrc);
        deferredImage.removeAttribute('data-src');
      }

      deferredImage.loading = 'eager';
      deferredImage.removeAttribute('fetchpriority');
      deferredImage.removeAttribute('data-deferred-image');
    }

    const deferredVideo = mediaRoot.querySelector('video[data-deferred-video]');
    if (deferredVideo) {
      const deferredPoster = deferredVideo.getAttribute('data-poster');
      if (deferredPoster) {
        deferredVideo.setAttribute('poster', deferredPoster);
        deferredVideo.removeAttribute('data-poster');
      }

      const deferredVideoSrc = deferredVideo.getAttribute('data-src');
      if (deferredVideoSrc) {
        deferredVideo.setAttribute('src', deferredVideoSrc);
        deferredVideo.removeAttribute('data-src');
      }

      deferredVideo.load();
      deferredVideo.removeAttribute('data-deferred-video');
      tryAutoplayHeroVideo(deferredVideo);
    }
  };

  const observeMediaRoots = (scope = document) => {
    const mediaRoots = scope.querySelectorAll(
      '[data-image-with-content-banner] [data-hero-media-item], [data-deferred-media-root]'
    );

    for (const mediaRoot of mediaRoots) {
      if (observedMediaRoots.has(mediaRoot) || hydratedMediaRoots.has(mediaRoot)) continue;

      observedMediaRoots.add(mediaRoot);

      if (!mediaObserver) {
        hydrateMediaRoot(mediaRoot);
        continue;
      }

      mediaObserver.observe(mediaRoot);
    }
  };

  const getFirstCarouselSlide = (slideshow) =>
    slideshow.querySelector('.swiper-wrapper > .swiper-slide');

  const shouldLoadCarouselSlideMedia = (slide, slideshow) => {
    const isFirstSlide = slide === getFirstCarouselSlide(slideshow);
    const isVisible = slide.classList.contains('swiper-slide-visible');
    const swiperReady = slideshow.dataset.initialized === 'true';

    if (isVisible) return true;
    if (isFirstSlide && !swiperReady) return true;

    return false;
  };

  const hydrateVisibleHeroSlides = (slideshow) => {
    if (!(slideshow instanceof HTMLElement)) return;

    const slides = slideshow.querySelectorAll('.swiper-wrapper > .swiper-slide');

    for (const slide of slides) {
      if (!shouldLoadCarouselSlideMedia(slide, slideshow)) continue;

      for (const mediaRoot of slide.querySelectorAll('[data-hero-media-item]')) {
        hydrateMediaRoot(mediaRoot);
      }

      for (const activeVideo of slide.querySelectorAll('video[data-hero-card-video]')) {
        tryAutoplayHeroVideo(activeVideo);
      }
    }

    for (const slide of slides) {
      if (shouldLoadCarouselSlideMedia(slide, slideshow)) continue;

      for (const inactiveVideo of slide.querySelectorAll(
        'video[data-hero-card-video], video[data-deferred-video]'
      )) {
        inactiveVideo.pause();
      }
    }
  };

  const bindHeroSlideshowMedia = (scope = document) => {
    const slideshows = scope.querySelectorAll(
      '[data-image-with-content-banner] slideshow-container'
    );

    for (const slideshow of slideshows) {
      if (slideshow.dataset.heroMediaBound === 'true') continue;

      slideshow.dataset.heroMediaBound = 'true';

      const syncVisibleSlides = () => {
        hydrateVisibleHeroSlides(slideshow);
      };

      for (const eventName of [
        'swiper:init',
        'swiper:slideChange',
        'swiper:slideChangeTransitionStart',
      ]) {
        slideshow.addEventListener(eventName, syncVisibleSlides);
      }

      syncVisibleSlides();

      if (slideshow.dataset.initialized !== 'true') {
        window.setTimeout(syncVisibleSlides, 100);
      }

      requestAnimationFrame(syncVisibleSlides);
    }
  };

  const onReady = () => {
    if ('IntersectionObserver' in window) {
      mediaObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const mediaRoot = entry.target;
            mediaObserver.unobserve(mediaRoot);
            hydrateMediaRoot(mediaRoot);
          }
        },
        {
          root: null,
          rootMargin: '200px 0px',
          threshold: 0.01,
        }
      );
    }

    observeMediaRoots();
    bindHeroSlideshowMedia();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const sectionContainer = event.target instanceof HTMLElement ? event.target : document;
    observeMediaRoots(sectionContainer);
    bindHeroSlideshowMedia(sectionContainer);
  });
})();
